import {
  Component,
  OnInit,
  TemplateRef,
  ChangeDetectionStrategy,
  input,
  model,
  computed,
  effect,
  contentChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { AgxPaginatorComponent } from '../agx-paginator/agx-paginator.component';

export interface ColumnMapping {
  property: string;
  heading: string;
  sticky?: boolean;
}

export interface AggregatedColumn {
  property: string;
  aggregator: (iterable: any[]) => any;
  heading: string;
  hidden?: boolean;
}

export interface StickyRow {
  properties: string[];
  values?: any[];
  aggregator: (iterable: any[]) => string | number;
  heading: string;
}

interface Column {
  property: string;
  value: string | number;
  rowspan: number;
}

const isObject = (object: object) => {
  return typeof object === 'object' && !Array.isArray(object);
};

@Component({
  selector: 'agx-pivot-table',
  templateUrl: './agx-pivot-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, NgTemplateOutlet, AgxPaginatorComponent],
})
export class AgxPivotTableComponent implements OnInit {
  constructor() {
    effect(() => {
      this.data();
      this.selectedColumns();
      if (!this._ready) {
        return;
      }
      this.buildViewModel();
    });
  }

  readonly template = contentChild<TemplateRef<any>>('customContent');

  readonly data = input<any[]>([]);

  readonly columnMappings = input<ColumnMapping[]>(undefined);

  readonly aggregatedColumns = input<AggregatedColumn[]>([]);

  readonly stickyRows = input<StickyRow[]>([]);

  readonly isFullWidth = input(false);

  readonly itemsPerPage = input(20);

  readonly isLoading = input(false);

  readonly enableColumnSelection = input(true);

  readonly enableCustomContent = input(false);

  readonly staticColumns = input(false);

  readonly selectedColumns = model<ColumnMapping[]>([]);

  readonly columnSelectionEnabled = computed(
    () => !this.staticColumns() && this.enableColumnSelection(),
  );

  private _ready = false;

  currentPage = 1;

  rows: Column[][] = [];

  groupedData: any = [];

  get totalItems() {
    return this.rows.length;
  }

  get pageLowerBound() {
    return this.currentPage * this.itemsPerPage() - this.itemsPerPage();
  }

  get pageUpperBound() {
    return this.currentPage * this.itemsPerPage();
  }

  get pageRows() {
    const pageRows = this.rows.slice(this.pageLowerBound, this.pageUpperBound);
    this.setColumnRowSpans(this.selectedColumns(), pageRows);
    return pageRows;
  }

  ngOnInit() {
    this.aggregatedColumns().push({
      property: 'id',
      aggregator: (iterable: number[]) => iterable,
      heading: 'ID',
      hidden: true,
    });
    this.rows = [];
    this.rows.push(
      this.aggregatedColumns().map((ac) => {
        const data = this.data().map((obj) => obj[ac.property]);
        return {
          property: ac.property,
          value: ac.aggregator(data),
          rowspan: 1,
          hidden: ac.hidden,
        };
      }),
    );
    this.buildViewModel();
    this.buildStickyRows();
    this._ready = true;
  }

  /**
   * Fired when the new column dropdown change event is fired,
   * adds to selected columns and rebuilds table rows
   * @param property The property to add to the selected columns
   */
  addSelectedColumn(property: string) {
    let mapping;
    if (property === 'id') {
      const index = this.aggregatedColumns().indexOf(
        this.aggregatedColumns().find(
          (ac: AggregatedColumn) => ac.property === 'id',
        ),
      );
      if (index !== -1) {
        this.aggregatedColumns().splice(index, 1);
      }
      mapping = {
        property: 'id',
        heading: 'ID',
        sticky: false,
      };
    } else {
      mapping = this.columnMappings().find((m) => m.property === property);
    }
    if (!this.isColumnSelected(mapping)) {
      this.selectedColumns().push(mapping);
      this.buildViewModel();
    }
  }

  /**
   * Removes a selected column and rebuilds the table rows
   * @param mapping The ColumnMapping to remove from the selected rows
   */
  removeSelectedColumn(mapping: ColumnMapping) {
    if (mapping.property === 'id') {
      this.aggregatedColumns().push({
        property: 'id',
        aggregator: (iterable: number[]) => iterable,
        heading: 'ID',
        hidden: true,
      });
    }
    const selectedColumns = this.selectedColumns();
    selectedColumns.splice(selectedColumns.indexOf(mapping), 1);
    this.buildViewModel();
  }

  /**
   * Change an existing selected column to a new column
   */
  onSelectedColumnChange(index: number, property: string) {
    const mapping = this.columnMappings().find((m) => m.property === property);
    if (!this.isColumnSelected(mapping)) {
      this.selectedColumns()[index] = mapping;
      this.buildViewModel();
    }
  }

  /**
   * Move a specified column one index to the right
   * @param index The index of the column to shift
   */
  moveColumnRight(index: number) {
    const selectedColumns = this.selectedColumns();
    const temp = selectedColumns[index];
    selectedColumns[index] = selectedColumns[index + 1];
    selectedColumns[index + 1] = temp;
    this.buildViewModel();
  }

  /**
   * Move a specified column one index to the left
   * @param index The index of the column to shift
   */
  moveColumnLeft(index: number) {
    const selectedColumns = this.selectedColumns();
    const temp = selectedColumns[index];
    selectedColumns[index] = selectedColumns[index - 1];
    selectedColumns[index - 1] = temp;
    this.buildViewModel();
  }

  isColumnSelected(columnMapping: ColumnMapping): boolean {
    const selectedColumns = this.selectedColumns();
    if (!selectedColumns || !columnMapping) {
      return false;
    }
    return (
      selectedColumns.findIndex(
        (selected) => selected.property === columnMapping.property,
      ) > -1
    );
  }

  onPageChange(pageNumber: number) {
    this.currentPage = pageNumber;
  }

  /**
   * Clear the column selections
   */
  clearSelections() {
    this.selectedColumns.set([]);
    this.buildViewModel();
  }

  /**
   * Build the table rows
   */
  buildViewModel() {
    const selectedColumns = this.selectedColumns();
    if (selectedColumns && selectedColumns.length > 0) {
      this.groupedData = this.nest(
        selectedColumns.map((c) => c.property),
        this.data(),
      );
      this.rows = [];
      this.traverseGroupedData(this.groupedData);
    } else {
      this.groupedData = [];
      this.rows = [];
      this.rows.push(
        this.aggregatedColumns().map((ac) => {
          const data = this.data().map((obj) => obj[ac.property]);
          return {
            property: ac.property,
            value: ac.aggregator(data),
            rowspan: 1,
            hidden: ac.hidden,
          };
        }),
      );
    }
  }

  /**
   * Recursively grouped data into a nested object based on property keys
   * @param keys The keys to group the data on
   * @param array The data to group
   */
  private nest(keys: string[], array: object[]) {
    if (keys.length === 0) {
      return array;
    }

    const first = keys[0];
    const rest = keys.slice(1);

    return this.mapValue(this.groupBy(first, array), (value: object[]) => {
      return this.nest(rest, value);
    });
  }

  /**
   * Get a copy of an object after a mutator has been applied to each property
   * @param object The object to copy
   * @param iteratee The property mutator
   */
  private mapValue(object: object, iteratee: any) {
    object = Object(object);
    const result = {};
    Object.keys(object).forEach((key) => {
      result[key] = iteratee(object[key]);
    });
    return result;
  }

  /**
   * Groups an array of objects based on a object key
   * @param key The object key to group the data on
   * @param array The array to group
   */
  private groupBy(key: string, array: object[]) {
    const objectsByKeyValue = {};
    // simple for loop for best performance
    for (let i = 0, l = array.length; i < l; i++) {
      let value = array[i][key];
      if (!value && key.includes('_accessObject_')) {
        // dig down into specified object(s)
        value = key
          .split('_accessObject_')
          .reduce((j, k) => j && j[k], array[i]);
      }
      if (typeof objectsByKeyValue[value] === 'undefined') {
        objectsByKeyValue[value] = [];
      }
      objectsByKeyValue[value].push(array[i]);
    }
    return objectsByKeyValue;
  }

  /**
   * Recursively traverse the grouped data and build table rows
   * @param data The grouped data object
   */
  private traverseGroupedData(data: object) {
    if (isObject(data)) {
      const keys = Object.keys(data);
      keys.forEach((key) => {
        if (isObject(data[key])) {
          this.traverseGroupedData(data[key]);
        } else {
          this.rows.push(
            this.selectedColumns()
              .map((column) => {
                let value = data[key][0][column.property];
                if (!value && column.property.includes('_accessObject_')) {
                  // If no value and property contains specific string indicating we want to dig down
                  // into a nested object, split on the string and traverse the object.
                  // Arbitrary levels of nesting is supported
                  value = column.property
                    .split('_accessObject_')
                    .reduce((j, k) => j && j[k], data[key][0]);
                }
                return {
                  property: column.property,
                  value,
                  rowspan: 1,
                };
              })
              .concat(
                this.aggregatedColumns().map((ac) => {
                  const groupedData = data[key].map(
                    (obj: object) => obj[ac.property],
                  );
                  return {
                    property: ac.property,
                    value: ac.aggregator(groupedData),
                    rowspan: 1,
                    hidden: ac.hidden,
                  };
                }),
              ),
          );
        }
      });
    }
  }

  /**
   * Recursively set the column rowspans of a table based upon the occurrence of value in the data subset
   */
  private setColumnRowSpans(
    selectedColumns: ColumnMapping[],
    rows: Column[][],
  ) {
    if (!selectedColumns || !rows) {
      return;
    }

    if (selectedColumns.length > 0 && rows.length > 0) {
      const columnMapping = selectedColumns[0];
      const groupedRows = [];
      const processedValues = [];

      if (rows.length > 0) {
        rows.forEach((row) => {
          const column = row.find((c) => c.property === columnMapping.property);
          if (column) {
            if (processedValues.indexOf(column.value) === -1) {
              processedValues.push(column.value);
              groupedRows.push([row]);
            } else {
              groupedRows[groupedRows.length - 1].push(row);
              const first = groupedRows[groupedRows.length - 1][0].find(
                (c: Column) => c.property === column.property,
              );
              first.rowspan++;
              row.splice(row.indexOf(column), 1);
            }
          }
        });
      }

      selectedColumns = selectedColumns.slice(1);
      groupedRows.forEach((subset) =>
        this.setColumnRowSpans(selectedColumns, subset),
      );
    }
  }

  buildStickyRows() {
    const stickyRows = this.stickyRows();
    if (stickyRows) {
      // Reset sticky row values
      stickyRows.forEach((stickyRow) => {
        stickyRow.values = Array(
          this.aggregatedColumns().filter((ac) => !ac.hidden).length,
        ).fill('-');
      });

      // Loop though aggregated columns first so that we calculate
      // sticky row values in order of ACs.
      this.aggregatedColumns().forEach((ac, index) => {
        this.stickyRows().forEach((stickyRow) => {
          stickyRow.properties.forEach((property) => {
            if (property === ac.property) {
              const data = this.data().map((obj) => obj[ac.property]);
              // replace '-' with calculated value
              stickyRow.values.splice(index, 1, stickyRow.aggregator(data));
            }
          });
        });
      });
    }
  }

  isAggregatedColumn(column: Column): boolean {
    return this.aggregatedColumns().some(
      (ac: AggregatedColumn) => ac?.property === column?.property,
    );
  }
}
